// Registro de viajeros (RD 933/2021): textos y reglas compartidos por el panel y el pre-checkin.
// «Lo que falta» repite las reglas de backend/registry/people.py (missing_for) para avisar antes de enviar;
// manda siempre el servidor.

export const ESTADO_COMUNICACION = {
  pending_data: { label: "Faltan datos", cls: "bg-[#E89B33]/15 text-[#9C6512]" },
  ready: { label: "Lista", cls: "bg-marca/10 text-marca" },
  sending: { label: "Enviando…", cls: "bg-marca/10 text-marca" },
  sent: { label: "Enviada, esperando a los Mossos", cls: "bg-marca/10 text-marca" },
  accepted: { label: "Aceptada", cls: "bg-[#4A7C59]/15 text-[#2F5538]" },
  rejected: { label: "Rechazada", cls: "bg-acento/15 text-[#8C3B26]" },
  error: { label: "Error de envío", cls: "bg-acento/15 text-[#8C3B26]" },
  filed: { label: "Presentada en SES", cls: "bg-[#4A7C59]/15 text-[#2F5538]" },
  manual_required: { label: "Pendiente de comunicar a mano", cls: "bg-[#E89B33]/15 text-[#9C6512]" },
  done_manually: { label: "Comunicada a mano", cls: "bg-[#4A7C59]/15 text-[#2F5538]" },
  discarded: { label: "Descartada", cls: "bg-[#EAE7E0] text-[#5C5C5C]" },
  superseded: { label: "Sustituida", cls: "bg-[#EAE7E0] text-[#5C5C5C]" },
};
export const OPERACION = { reservation: "Reserva", delivery: "Entrega", cancellation: "Anulación", modification: "Modificación" };
export const CANAL = { mossos: "Mossos d'Esquadra", ses: "SES.HOSPEDAJES" };
// Lo ya comunicado (backend/registry/service.py, COMMUNICATED): lo que falta ya no cuenta para ello.
const COMUNICADAS = new Set(["sent", "accepted", "filed", "done_manually"]);
export const comunicada = (c) => COMUNICADAS.has(c?.status);
// Lo que falta que pone la propia entrega (backend/registry/context.py, KM_MISSING y HANDOVER_TIME_MISSING): antes de
// entregar no se pide en ningún otro sitio.
export const LO_PONE_LA_ENTREGA = ["Falta el kilometraje de la entrega", "Falta la fecha y hora de la entrega"];
export const sinLoDeLaEntrega = (lista) => (lista || []).filter((t) => !LO_PONE_LA_ENTREGA.includes(t));
// Cobros fuera de la plataforma (backend/registry/payments.py, EXTERNAL_METHODS).
export const METODO_COBRO = { cash: "Efectivo", transfer: "Transferencia", pos: "TPV (tarjeta en la oficina)",
  platform: "Otra plataforma de alquiler", other: "Otro medio" };
export const PAGO_TEXTO = { ...METODO_COBRO, card: "Tarjeta", at_destination: "Pago en destino", mobile: "Pago por móvil",
  gift: "Tarjeta regalo" };
const FINALES = new Set(["accepted", "filed", "done_manually", "discarded", "superseded"]);
// Lo enviado al entorno de pruebas de los Mossos (`environment: "PRE"`) no es una comunicación real (no cuenta para
// conservar la reserva): mientras los ajustes estén en PRE, la página del registro y los ajustes lo avisan.
export const AVISO_PRE = ("Entorno de pruebas de los Mossos: lo que se envía no cuenta como comunicado. Hasta tener "
  + "las claves reales, comunica las reservas reales en el formulario de Canal Empresa.");
export const enPruebas = (c) => c?.environment === "PRE";

export function estadoComunicacion(c) {
  if (c?.status === "ready") return c.channel === "ses" ? "Lista para subir a SES" : "En cola de envío";
  return ESTADO_COMUNICACION[c?.status]?.label || c?.status || "—";
}
export const claseEstado = (c) => ESTADO_COMUNICACION[c?.status]?.cls || "bg-[#EAE7E0] text-[#5C5C5C]";
export const esFinal = (c) => FINALES.has(c?.status);
// Lo que pide atención lo decide el servidor (`ATTENTION_QUERY`, con `?attention=1`); aquí no se repite.

// Resultado de cada intento de envío a los Mossos (backend/registry/jobs.py y mossos_client.py).
export const RESULTADO_INTENTO = { sent: "Enviada", invalid: "No admitida", auth: "Credenciales no aceptadas",
  config: "Error de configuración", transient: "Sin respuesta", unverified: "Sin poder comprobar si llegó la anterior",
  adopted: "Ya estaba en los Mossos", interrupted: "Envío interrumpido",
  doubtful: "No se sabe si llegó: compruébalo en el portal de los Mossos" };

// Lo que ha hecho «Comprobar ahora» (`POST /admin/registry/run`, backend/registry/jobs.py `tick`): lo recuperado por
// la conciliación (`reconciled`), lo que ha quedado listo, lo enviado y lo consultado en los Mossos.
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
export function resumenComprobacion(r) {
  if (!r || typeof r !== "object") return "Comprobado.";
  const n = (k) => Math.max(0, Number(r[k]) || 0);
  const partes = [
    n("reconciled") && plural(n("reconciled"), "comunicación recuperada", "comunicaciones recuperadas"),
    n("prepared") && plural(n("prepared"), "preparada", "preparadas"),
    n("sent") && plural(n("sent"), "enviada a los Mossos", "enviadas a los Mossos"),
    n("polled") && plural(n("polled"), "consultada en los Mossos", "consultadas en los Mossos"),
  ].filter(Boolean);
  if (partes.length === 0) return "Comprobado: no había nada que preparar, enviar ni consultar.";
  const lista = partes.length > 1 ? `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}` : partes[0];
  return `Comprobado: ${lista}.`;
}

export const ALTAS = ["reservation", "delivery"];

// Una alta de SES que llegó a comunicarse sin el código de comunicación que da SES: presentada, hecha a mano o
// sustituida después de una de las dos. Su anulación (B) lo necesita; lo que admite `service.mark_filed`. Nunca una
// B: no tiene anulación que lo necesite.
export function faltaCodigoSes(c) {
  if (c?.channel !== "ses" || !ALTAS.includes(c.operation) || c.ses?.codigo_comunicacion) return false;
  return c.status === "filed" || c.status === "done_manually"
    || (c.status === "superseded" && Boolean(c.ses?.filed_at || c.manual?.done_at));
}

// La anulación (B) de SES que espera el código de la reserva presentada (`original_id`): `_prepare_ses_cancellation`
// la deja pendiente con eso como única falta (un XML que no valida va a `validation_errors`).
export const bSinCodigo = (c) => c?.channel === "ses" && c.operation === "cancellation" && c.status === "pending_data"
  && Boolean(c.original_id) && (c.missing || []).length > 0;

// Una alta de los Mossos que llegó (tiene `id_comunicacio`) y no se admitió: rechazada o con error.
export const llegoSinAdmitir = (c) => c?.channel === "mossos" && ["rejected", "error"].includes(c?.status)
  && Boolean(c?.remote?.id_comunicacio);
// Si su reserva se anula (`reservation_cancelled`, de la API), no se reintenta y `service._cancel_one` le deriva
// siempre una anulación a mano: la gestión va en esa anulación, con una sola instrucción.
export const gestionadaConAnulacion = (c, anulada = Boolean(c?.reservation_cancelled)) => anulada && llegoSinAdmitir(c);
export const GESTIONADA_CON_ANULACION = "Se gestiona con la anulación a mano";
// Lo que dice la fila de una anulación a mano de los Mossos sin motivo (`manual.reason`) guardado.
export const ANULAR_A_MANO = "Anula la comunicación en el formulario de Canal Empresa de los Mossos y márcala como hecha";
// La alta pudo llegar o no (envío sin respuesta): la anulación lleva `manual.uncertain` y su motivo acaba con esto
// (backend/registry/service.py, MANUAL_CANCEL_UNCERTAIN); la confirmación de «Marcar como hecha» lo repite.
export const COMPROBAR_SI_LLEGO = "Comprueba antes en el portal de los Mossos si llegó; si no llegó, márcala como hecha sin más.";
// Lo que lee el operador donde el administrador lee qué hacer con sus botones (marcar a mano, comprobar en el portal,
// añadir el código de SES): el operador no tiene esas acciones (backend/roles.py) y solo lee el registro.
export const A_MANO_ADMIN = "Queda por hacer a mano: lo hace un administrador del panel.";
export const SIN_RESPUESTA_ADMIN = "Los Mossos no han dado el resultado: lo comprueba un administrador del panel.";
export const CODIGO_SES_ADMIN = "Falta el código de SES de la reserva presentada: lo añade un administrador del panel.";
// El aviso tras descargar el XML de SES (backend/registry/service.py, SES_DOWNLOADED_WARNING) es para quien lo
// descargó y habla de un botón del administrador: el operador no lo lee.
export const AVISO_XML_DESCARGADO = "Ya descargaste una versión anterior de este XML: si la subiste a SES, márcala como "
  + "presentada antes de subir esta";
export const avisosDe = (c, admin = true) => (c?.warnings || []).filter((w) => admin || w !== AVISO_XML_DESCARGADO);

export function provinciaDeCP(cp) {
  const s = String(cp || "").trim();
  if (!/^\d{5}$/.test(s)) return null;
  const p = Number(s.slice(0, 2));
  return p >= 1 && p <= 52 ? s.slice(0, 2) : null;
}

export function nombrePartido(full) {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: "", last_name1: "", last_name2: "" };
  if (parts.length === 1) return { first_name: parts[0], last_name1: "", last_name2: "" };
  if (parts.length === 2) return { first_name: parts[0], last_name1: parts[1], last_name2: "" };
  return { first_name: parts.slice(0, -2).join(" "), last_name1: parts[parts.length - 2], last_name2: parts[parts.length - 1] };
}

export const direccionVacia = () => ({ country: "ESP", street_type: "c.", street_name: "", number: "", staircase: "",
  floor: "", door: "", postal_code: "", municipality_code: "", municipality_name: "" });

// Los valores por defecto según el tipo de documento (especificación §4.4; no se inventan datos para la policía):
// - DNI: nacionalidad, país del documento y país del carné, España (el del documento, fijo);
// - NIE: el país del documento es España (fijo); la nacionalidad, sin valor (quien tiene NIE no es español);
// - pasaporte u otro: nada; el cliente elige los suyos.
// Al cambiar de tipo se vacía lo que se puso por defecto y ya no encaja; lo que eligió el cliente y encaja se queda.
const DOC_ESPANOL = ["NIF", "NIE"];
export function conTipoDocumento(p, tipo) {
  const antes = p?.doc_type;
  const next = { ...p, doc_type: tipo };
  const lic = p?.license && typeof p.license === "object" ? p.license : null;
  if (DOC_ESPANOL.includes(tipo)) next.doc_country = "ESP";
  else if (DOC_ESPANOL.includes(antes)) next.doc_country = "";
  if (tipo === "NIF") next.nationality = "ESP";
  else if (next.nationality === "ESP" && (antes === "NIF" || tipo === "NIE")) next.nationality = "";
  if (lic && tipo === "NIF" && !lic.country) next.license = { ...lic, country: "ESP" };
  else if (lic && tipo !== "NIF" && antes === "NIF" && lic.country === "ESP") next.license = { ...lic, country: "" };
  return next;
}

export function personaVacia(docType = "NIF") {
  const vacia = { first_name: "", last_name1: "", last_name2: "", sex: "", nationality: "", doc_type: "",
    doc_country: "", doc_number: "", birth_date: "", address: direccionVacia(),
    license: { license_class: "B", number: "", expiry: "", country: "", support_number: "" }, phone: "", email: "" };
  return conTipoDocumento(vacia, docType);
}

// Como RegistryAddress del backend: en España, un código postal válido y un municipio del INE de su provincia; fuera,
// la localidad, y el código postal puede faltar (SES lo admite vacío; los Mossos lo piden en la entrega, ver `faltan`).
function direccionCompleta(a) {
  if (!a || !a.street_name) return false;
  if (a.country !== "ESP") return Boolean(a.municipality_name);
  const prov = provinciaDeCP(a.postal_code);
  return Boolean(prov && String(a.municipality_code || "").startsWith(prov));
}
const carneCompleto = (l) => Boolean(l && l.license_class && l.number && l.expiry && l.country);
// Como `strict_alnum_upper` del backend, que mide los topes después de normalizar: sin espacios, guiones, puntos,
// barras, comas, subrayados ni tildes.
const docNormalizado = (n) => String(n || "").normalize("NFKD").replace(/[\s./\\,_\p{Pd}\p{M}]/gu, "");

// Un carné o una dirección a medias no se mandan: el servidor los rechazaría enteros. En modo básico (sin canal),
// solo lo que el formulario enseña y el cliente ha rellenado: nada de los valores por defecto de lo que no se pinta
// (nacionalidad, tipo y país del documento, clase y país del carné…); al segundo conductor no se le pide dirección,
// teléfono ni correo.
const VISIBLES_BASICO = ["first_name", "last_name1", "last_name2", "doc_number", "birth_date"];
export function limpiarPersona(p, { basico = false, titular = true } = {}) {
  const limpia = { ...p, license: p.license && p.license.number ? p.license : null,
    address: p.address && p.address.street_name ? p.address : null };
  if (!basico) return limpia;
  const out = {};
  for (const k of titular ? [...VISIBLES_BASICO, "phone", "email"] : VISIBLES_BASICO) if (p[k]) out[k] = p[k];
  if (titular && limpia.address) out.address = limpia.address;
  if (limpia.license) out.license = { number: limpia.license.number };
  return out;
}

// Las dos frases de `faltan` que no son una falta sino un dato que no vale (`revisarPersona` las separa).
const SEXO_MOSSOS = "los Mossos solo admiten hombre o mujer en el sexo";
const DOC_SES_LARGO = "un número de documento de 15 caracteres como máximo";

export function faltan(p, { canal, entrega = true } = {}) {
  const out = [];
  const need = (ok, texto) => { if (!ok) out.push(texto); };
  need(p?.first_name, "el nombre");
  need(p?.last_name1, "el primer apellido");
  if (p?.doc_type === "NIF") need(p?.last_name2, "el segundo apellido");
  if (canal === "mossos") need(p?.phone || p?.email, "un teléfono o un correo");
  if (entrega) {
    need(p?.birth_date, "la fecha de nacimiento");
    if (canal === "mossos" && p?.sex === "O") out.push(SEXO_MOSSOS);
    else need(p?.sex, "el sexo");
    need(p?.nationality, "la nacionalidad");
    need(p?.doc_type, "el tipo de documento");
    need(p?.doc_number, "el número de documento");
    if (canal === "mossos") need(p?.doc_country, "el país del documento");
    if (canal === "ses" && docNormalizado(p?.doc_number).length > 15) out.push(DOC_SES_LARGO);
    if (!direccionCompleta(p?.address)) out.push("la dirección completa");
    else if (canal === "mossos") need(p.address.postal_code, "el código postal de la dirección (los Mossos lo exigen)");
    need(carneCompleto(p?.license), "los datos del carné");
  }
  return out;
}

// Sin canal: lo mínimo de siempre del pre-checkin (el titular, también nacimiento, teléfono y dirección). El nombre
// basta (con «Nombre y apellidos» pasaba quien tiene uno solo). La dirección, con su localidad, que va en la línea
// firmada: la vía y, en España, el código postal y el municipio de la lista; fuera, la localidad (el código postal
// puede faltar).
export function faltanBasicos(p, { titular = true } = {}) {
  const out = [];
  const need = (ok, texto) => { if (!ok) out.push(texto); };
  need(p?.first_name, "el nombre");
  need(p?.doc_number, "el número de documento");
  need(p?.license?.number, "el número del carné");
  if (titular) {
    need(p?.birth_date, "la fecha de nacimiento");
    need(p?.phone, "el teléfono");
    need(direccionCompleta(p?.address), "la dirección");
  }
  return out;
}

// El nombre de la vía ya empieza por su tipo («Calle Mayor», «C/ Mayor» con «c.»): por su abreviatura, por su nombre
// en castellano (la tabla `street_types`, «Calle (c.)») o, en los frecuentes, en catalán. Palabra a palabra, sin
// tildes ni mayúsculas («Calvo Sotelo» no empieza por «c.»).
const VIA_EN_CATALAN = { "c.": "carrer", "av.": "avinguda", "pl.": "plaça", "pg.": "passeig", "ptge.": "passatge",
  "urb.": "urbanització", "pol.": "polígon", "gv": "gran via", "trav.": "travessera" };
const palabras = (t) => String(t || "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().split(/[\s./]+/)
  .filter(Boolean);
function empiezaPorElTipo(nombre, tipo, tipos) {
  const n = palabras(nombre);
  const enTabla = Array.isArray(tipos) ? tipos.find((t) => t?.code === tipo) : null;
  const formas = [tipo, VIA_EN_CATALAN[tipo], String(enTabla?.label || "").replace(/\s*\([^)]*\)\s*$/, "")];
  return formas.map(palabras).some((f) => f.length > 0 && f.every((w, i) => n[i] === w));
}

// «C. MAJOR 12, 2 1, 08001 Barcelona». Fuera de España lleva el país: con `paises` (la tabla `countries` de
// las tablas del registro), su nombre; sin ella, el código. Sin canal es lo que firma el cliente: el tipo de vía no
// se repite si el nombre ya empieza por él (`tipos`, la tabla `street_types`), y «S/N» solo si no hay número, ni en
// su campo ni escrito con el nombre.
export function lineaDireccion(a, paises, tipos) {
  if (!a) return "";
  const repetido = empiezaPorElTipo(a.street_name, a.street_type, tipos);
  const tipo = a.street_type && !repetido ? a.street_type.toUpperCase() : "";
  const numero = a.number || (/\d/.test(a.street_name || "") ? "" : "S/N");
  const via = [tipo, a.street_name, numero].filter(Boolean).join(" ");
  const extra = [a.staircase, a.floor, a.door].filter(Boolean).join(" ");
  const lugar = [a.postal_code, a.municipality_name].filter(Boolean).join(" ");
  const pais = a.country && a.country !== "ESP"
    ? (Array.isArray(paises) && paises.find((c) => c.code === a.country)?.name) || a.country : "";
  return [extra ? `${via}, ${extra}` : via, lugar, pais].filter(Boolean).join(", ");
}

// Los campos de siempre del pre-checkin (contrato, correos, OCR), sacados de los del registro. `refdata` (opcional)
// da el nombre del país de una dirección extranjera. Sin correo, `null`: el backend lo admite opcional, no vacío.
export function camposDeSiempre(p, refdata) {
  return {
    full_name: [p.first_name, p.last_name1, p.last_name2].filter(Boolean).join(" ").trim(),
    dni_number: String(p.doc_number || "").toUpperCase(),
    birth_date: p.birth_date || "",
    address: lineaDireccion(p.address, refdata?.countries, refdata?.street_types),
    license_number: p.license?.number || "",
    phone: p.phone || "",
    email: String(p.email || "").trim() || null,
  };
}

const NIE = /^[XYZ]\d{7}[A-Z]$/;
const NIF = /^\d{8}[A-Z]$/;
// Lo leído de un documento. Sin `sobrescribir` (el segundo conductor, que se aplica solo) rellena lo vacío; con él
// (el titular pulsa «Aplicar a mis datos») cambia el documento, el nacimiento y el número del carné. El nombre solo
// se propone si está vacío, y nunca el del carné: allí son «apellidos y nombre» en una cadena (campos 1 y 2) y
// repartirlo sería adivinar. La dirección leída no se aplica: es una línea, no la dirección estructurada.
export function aplicarOcr(p, campos, { sobrescribir = false, origen } = {}) {
  const pisa = (actual) => sobrescribir || !actual;
  const next = { ...p, license: { ...(p.license || {}) } };
  if (campos.full_name && origen !== "license" && !p.first_name && !p.last_name1) {
    Object.assign(next, nombrePartido(campos.full_name));
  }
  if (campos.dni_number && pisa(p.doc_number)) {
    const n = String(campos.dni_number).toUpperCase().replace(/[^0-9A-Z]/g, "");
    next.doc_number = n;
    // Con los valores por defecto de su tipo (`conTipoDocumento`): un NIE no deja la nacionalidad española del DNI.
    if (NIE.test(n)) Object.assign(next, conTipoDocumento(next, "NIE"));
    else if (NIF.test(n)) Object.assign(next, conTipoDocumento(next, "NIF"));
  }
  if (campos.birth_date && pisa(p.birth_date)) next.birth_date = campos.birth_date;
  if (campos.license_number && pisa(next.license.number)) next.license.number = String(campos.license_number).toUpperCase();
  return next;
}

// Qué ha cambiado al aplicar el OCR, para decirlo (y no decir «aplicado» si ya coincidía).
export function cambiosOcr(antes, despues) {
  const out = [];
  const distinto = (...ks) => ks.some((k) => (antes?.[k] || "") !== (despues?.[k] || ""));
  if (distinto("first_name", "last_name1", "last_name2")) out.push("el nombre");
  if (distinto("doc_type", "doc_number")) out.push("el documento");
  if (distinto("birth_date")) out.push("la fecha de nacimiento");
  if ((antes?.license?.number || "") !== (despues?.license?.number || "")) out.push("el número del carné");
  return out;
}

// ---------- Lo que pide el pre-checkin a cada persona ----------
// Con canal, lo del registro (`faltan`) y, para el titular, el teléfono de siempre; sin canal, lo de siempre
// (`faltanBasicos`). `revisar` son los datos que están pero el servidor rechazaría (backend/registry/people.py y
// registry/checkin.py): mejor decirlo antes de firmar que con un 422 al final.
const LETRAS_DNI = "TRWAGMYFPDXBNJZSQVHLCKE";
const EMAIL = /^[^@\s<>,;]+@[^@\s<>,;]+\.[^@\s<>,;]+$/;
const CORTOS = [["staircase", "la escalera"], ["floor", "el piso"], ["door", "la puerta"]];
const hoyIso = () => new Date().toISOString().slice(0, 10);
const soloLatinas = (n) => !/[^0-9A-Z]/.test(docNormalizado(n).toUpperCase());

// Como `phone_digits`: con prefijo (+ o 00), o un número español de 9 cifras; de 8 a 15 cifras.
export function telefonoValido(phone) {
  let raw = String(phone || "").trim();
  if (raw.startsWith("+") || raw.startsWith("00")) raw = raw.replace(/\(\s*0\s*\)/g, " ");
  let d = raw.replace(/[^0-9]/g, "");
  if (raw.startsWith("00")) d = d.slice(2);
  else if (!raw.startsWith("+")) {
    if (d.length !== 9 || !"6789".includes(d[0])) return false;
    d = `34${d}`;
  }
  return d.length >= 8 && d.length <= 15;
}

// Como `_spanish_id`: el tipo que corresponde al número y la letra de control. `null` si vale (o no es DNI/NIE).
function errorDocumento(tipo, numero) {
  const n = docNormalizado(numero).toUpperCase();
  if (!n) return null;
  if (/[^0-9A-Z]/.test(n)) return "el número de documento (solo letras latinas y cifras)";
  let cifras;
  let letra;
  if (tipo === "NIF") {
    if ("XYZ".includes(n[0])) return "el tipo de documento (ese número es de un NIE)";
    if ("KLM".includes(n[0])) return "el tipo de documento (un número que empieza por K, L o M no es un DNI: elige pasaporte u otro)";
    const m = /^([0-9]{7,8})([A-Z])$/.exec(n);
    if (!m) return "el número de DNI (8 cifras y una letra)";
    [, cifras, letra] = m;
  } else if (tipo === "NIE") {
    if (/^[0-9]{8}[A-Z]$/.test(n)) return "el tipo de documento (ese número es de un DNI)";
    const m = /^([XYZ])([0-9]{7,8})([A-Z])$/.exec(n);
    if (!m || (m[2].length === 8 && m[2][0] !== "0")) return "el número de NIE (X, Y o Z, 7 cifras y una letra)";
    cifras = `${"XYZ".indexOf(m[1])}${m[2].slice(-7)}`;
    letra = m[3];
  } else {
    return null;
  }
  return LETRAS_DNI[Number(cifras) % 23] === letra ? null : "la letra del DNI o NIE, que no corresponde a las cifras";
}

// Escalera, piso y puerta: hasta 2 letras o cifras, sin contar «º» ni «ª» (como `_short`).
export const cortoLimpio = (v) => String(v || "").replace(/[ºª°]/g, "").toUpperCase();

function formatoRegistro(p, recogida) {
  const out = [];
  const add = (texto) => { if (texto) out.push(texto); };
  add(errorDocumento(p?.doc_type, p?.doc_number));
  if (p?.license?.number && !soloLatinas(p.license.number)) add("el número del carné (solo letras latinas y cifras)");
  for (const [k, nombre] of CORTOS) {
    const v = docNormalizado(cortoLimpio(p?.address?.[k])).toUpperCase();
    if (v && !/^[0-9A-Z]{1,2}$/.test(v)) add(`${nombre} (hasta 2 letras o cifras)`);
  }
  // El número de la vía conserva el guion («12-14», que va tal cual en la línea firmada sin canal); el registro solo
  // admite cifras (como `RegistryAddress.number`).
  const numero = String(p?.address?.number || "").trim();
  if (numero && !/^[0-9]{1,5}$/.test(numero)) {
    add("el número de la vía (solo cifras, hasta 5: si son dos, como «12-14», escribe el primero)");
  }
  if (p?.phone && !telefonoValido(p.phone)) add("el teléfono (con el prefijo internacional, p. ej. +34 600 000 000)");
  if (p?.birth_date && (p.birth_date < "1900-01-01" || p.birth_date > hoyIso())) {
    add("la fecha de nacimiento, que está fuera de rango");
  }
  const pickup = String(recogida || "").slice(0, 10);
  if (p?.license?.expiry && pickup && p.license.expiry < pickup) add("el carné, que caduca antes de la recogida");
  return out;
}

export function revisarPersona(p, { canal, titular = true, recogida } = {}) {
  const correo = (canal || titular) && p?.email && !EMAIL.test(String(p.email).trim()) ? ["el correo, que no es válido"] : [];
  if (!canal) return { faltan: faltanBasicos(p, { titular }), revisar: correo };
  const todo = faltan(p, { canal });
  let lista = todo.filter((t) => t !== SEXO_MOSSOS && t !== DOC_SES_LARGO);
  if (titular && !p?.phone) lista = [...lista.filter((t) => t !== "un teléfono o un correo"), "el teléfono"];
  const revisar = [...todo.filter((t) => t === SEXO_MOSSOS || t === DOC_SES_LARGO), ...formatoRegistro(p, recogida),
    ...correo];
  return { faltan: lista, revisar };
}

// Los campos que se exigen a una persona, para marcarlos en el formulario (claves de PersonFields; las de la
// dirección y el carné con su prefijo). Repite las reglas de `faltan` y `faltanBasicos`; «un teléfono o un correo»
// (el segundo conductor con los Mossos) no marca ninguno de los dos.
export function obligatorios(p, { canal, titular = true } = {}) {
  const out = new Set(["first_name", "doc_number", "license.number"]);
  const add = (...ks) => ks.forEach((k) => out.add(k));
  const espana = (p?.address?.country || "ESP") === "ESP";
  if (!canal) {
    if (titular) add("birth_date", "phone", "address.street_name", "address.municipality");
    if (titular && espana) add("address.postal_code");
    return out;
  }
  add("last_name1", "birth_date", "sex", "nationality", "doc_type", "address.country", "address.street_name",
    "address.municipality", "license.license_class", "license.expiry", "license.country");
  if (p?.doc_type === "NIF") add("last_name2");
  if (titular) add("phone");
  if (canal === "mossos") add("doc_country");
  if (espana || canal === "mossos") add("address.postal_code");
  return out;
}

// «vence en 5 h», «vencida hace 20 min». Hacia abajo: a 1 h 30, «vence en 1 h» (un plazo no se da por más largo de
// lo que es). Sin plazo (o con una fecha que no se entiende), cadena vacía.
export function cuentaAtras(dueIso, ahora = Date.now()) {
  const ms = Date.parse(dueIso) - ahora;
  if (!Number.isFinite(ms)) return "";
  const abs = Math.abs(ms);
  const min = Math.floor(abs / 60000);
  const texto = min >= 60 ? `${Math.floor(min / 60)} h` : `${Math.max(1, min)} min`;
  return ms >= 0 ? `vence en ${texto}` : `vencida hace ${texto}`;
}
